import React, { useContext, useEffect, useState, useCallback } from "react";
import {
  DataGrid,
  Column,
  Scrolling,
  Paging,
} from "devextreme-react/data-grid";
import { useDataGridColumnWidths } from "../../../hooks/useDataGridColumnWidths";
import { MasterDetailTemplateData } from "devextreme/ui/data_grid";
import { createSitesClientApi, taxPortalClientApi, userAssignmentClientApi } from "../../../api-client-nswag/taxportal-client-runtime";
import {
  GetAllRolesDTO,
  ServiceBusEnum,
  ServiceMessage,
  SiteTrackRecordDTO,
} from "../../../api-client-nswag/taxportal-client";
import { UserAssignTpac, TpacRequestBasicMessage } from "../../../api-client-nswag/userassignment-client";
import {
  atom,
  useRecoilState,
  useRecoilValue,
} from "recoil";
import { Link } from "@mui/material";
import { AppName, dataGridConfig } from "../../../constants";
import { SnackbarMessage, useSnackbar } from "../../../hooks";
import { OidcIdentityContext } from "@rsmus/react-auth";
import { getExpandedRowData, getRoleData } from "../CreateSite&SetUp";
import { isEmpty, isNull } from "lodash";
import { convertUTCTimeToLocal } from "../../../util/Utils";
import { getAppName } from "../../../cui/commonComponents/AppNameToggle";
import { ProcessListTab } from "./ProcessListTab";

interface dataTypes {
  pending: boolean;
  data: SiteTrackRecordDTO[] | undefined;
  fullfilled: boolean;
}

export const statusState = atom({
  key: "statusState1",
  default: { siteId: null, status: "Active" },
});
interface statusType {
  siteId: number | null | any;
  status: string;
}
interface roleType {
  name?: string | undefined;
  RoleId?: string | undefined;
}
interface GetSiteSettingsDTO {
  id: string | number;
  setting: string;
  description: string;
  value: string;
  actionDisplayName: string;
  actionUrl: string;
}
export const SiteProcessData = (props: MasterDetailTemplateData) => {
  const [data, setData] = useState<dataTypes>({
    pending: false,
    data: [],
    fullfilled: false,
  });
 
  const { user, logout } = useContext(OidcIdentityContext);
  const siteId = useRecoilValue(getExpandedRowData);
  const { showSnackbar } = useSnackbar();
  const [status, setStatus] = useRecoilState<statusType>(statusState);
  const appName = useRecoilValue(getAppName);
  const roles = useRecoilValue(getRoleData);
  const [siteSettings, setSiteSettings] = useState<GetSiteSettingsDTO[]>([]);
  const [selectedTab, setSelectedTab] = useState<string>('tab1');


  // Global column width management
  const { gridRef, onColumnResized, onInitialized } = useDataGridColumnWidths({
    gridId: `siteProcessData-${siteId}`,
    defaultColumnWidths: {
      0: 400, // process column
      1: 100, // Run Process column
      2: 80,  // Status column
      3: 400, // Message column
      4: 200, // Initiated date and time column
      5: 200, // Completed date and time column
      6: 150  // Initiated by column (default width)
    }
  });

  const processToInclude = [
    "PROVIDE_USERGROUP_ACCESS",
    "TESS_ISHEET_ASSIGNMENT",
    "CLIENT_LEVEL_ROLE_ASSIGN",
    "INTERNAL_USER_ASSIGNMENT",
    "CREATE_EXTERNAL_USER",
    "CHECK_EXTERNAL_USER_STATUS",
    //"POPULATE_MEMBERFIRM_DROPDOWN",
    "MEMBER_FIRM",
    "CREATE_EXTERNAL_USER_THIRD_PARTY",
    "CHECK_THIRD_PARTY_USER_STATUS",
    //"CONTROVERSY_TRACKER",
  ];

  const buttonStyle = {
    cursor: "pointer",
    padding: "5px 16px",
    transition: "color 0.3s, transform 0.3s, box-shadow 0.3s",
    margin: "10px 10px 10px 0px",
    color: "#333",
    fontFamily: "Arial, sans-serif",
    fontSize: "14px",
    border: "none",
    backgroundColor: "transparent",
    position: "relative",
    overflow: "hidden",
    zIndex: 1,
    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
    borderRadius: "20px"
  };

  const activeButtonStyle: any = {
    ...buttonStyle,
    backgroundColor: "#009cde",
    color: "#fff",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
  };

  const activeUnderlineStyle: any = {
    position: "absolute",
    left: 0,
    bottom: 0,
    width: "100%",
    height: "2px",
    backgroundColor: "#009cde",
    transform: appName === "CS" ? "scaleX(1)" : "scaleX(0)",
    transformOrigin: "left",
    transition: "transform 0.3s",
    zIndex: -1,
  };

  const subProcessToInclude = [
    "L1Support",
    "TESS",
    "CLIENT_USER",
    "CS_ENGAGEMENT_TEAM",
    "CS_FUNCTIONAL_LEAD",
    "CS_PROJECT_LEAD",
    "CS_POWER_USER_DELEGATED",
    "CS_L2Support",
    "FUNCTIONAL_LEAD",
    "PROJECT_LEAD",
    "MEMBER_FIRM",
    "MEMBER_FIRM_STATUS",
    "RSM_ENGAGEMENT_TEAM",
    "POWER_USER_DELEGATED",
    "ASSIGN_USER_TO_SITE",
  ];

  const createProcess = (
    type: string,
    subType?: string
  ): SiteTrackRecordDTO => ({
    type,
    status: "",
    message: "",
    createdBy: "",
    created: undefined,
    lastModified: undefined,
    subType,
  });

  const ensureProcessExists = (
    processes: SiteTrackRecordDTO[],
    type: string,
    subTypes: string[] = []
  ) => {
    if (!processes.some((e) => e.type === type)) {
      if (subTypes.length > 0) {
        subTypes.forEach((subType) =>
          processes.push(createProcess(type, subType))
        );
      } else {
        processes.push(createProcess(type));
      }
    }
  };

  const processData = async () => {
    try {
      if (siteId !== null) {
        setData({ pending: true, data: [], fullfilled: false });
        const processDataStatus =
          await taxPortalClientApi.siteSetUp_SiteSetUpStatus(siteId);
        const processes = processDataStatus?.data || [];
        const filteredProcesses = filterProcesses(processes);
        ensureRequiredProcesses(filteredProcesses);
        setData({ pending: false, data: filteredProcesses, fullfilled: true });
        const response =
          await createSitesClientApi.taxportal_GetSiteProcessExecutionDetails(
            String(siteId),
            appName
          );
        const parentStatus = response?.data?.status;
        if (typeof parentStatus !== "string" || !parentStatus.trim()) {
          throw new Error("Process Execution API returned no parent status.");
        }
        setStatus({ siteId, status: parentStatus });
      }
    } catch (error) {
      console.error("Error processing data:", error);
      setData({ pending: false, data: [], fullfilled: false });
    }
  };

  const filterProcesses = (processes: any) => {
    return processes.filter((item: any) => {
      const includeProcess = processToInclude.includes(item.type);
      const internalSiteAssignmentCondition =
        !["PROVIDE_USERGROUP_ACCESS", "TESS_ISHEET_ASSIGNMENT"].includes(item.type) ||
        appName === AppName.IS;
      const clientLevelRoleCondition =
        item.type === "CLIENT_LEVEL_ROLE_ASSIGN" &&
        (item.subType === null || subProcessToInclude.includes(item.subType));
      const createExternalUserCondition =
        (item.type === "CREATE_EXTERNAL_USER" ||
          item.type === "MEMBER_FIRM" ||
          item.type === "CREATE_EXTERNAL_USER_THIRD_PARTY") &&
        (item.subType === null || item.subType === "ASSIGN_USER_TO_SITE");
      const excludeNullSubTypeCondition =
        item.type === "CLIENT_LEVEL_ROLE_ASSIGN" && item.subType === null;
      return (
        includeProcess &&
        internalSiteAssignmentCondition &&
        !clientLevelRoleCondition &&
        !createExternalUserCondition &&
        !excludeNullSubTypeCondition
      );
    });
  };

  const ensureRequiredProcesses = (processes: any) => {
    if (appName === AppName.IS) {
      ensureProcessExists(processes, "PROVIDE_USERGROUP_ACCESS");
      ensureProcessExists(processes, "TESS_ISHEET_ASSIGNMENT");
    }
    ensureProcessExists(processes, "CREATE_EXTERNAL_USER_THIRD_PARTY", [
      "IDM_AND_CEM",
    ]);
    ensureProcessExists(processes, "CREATE_EXTERNAL_USER", ["IDM_AND_CEM"]);
    ensureProcessExists(processes, "CHECK_EXTERNAL_USER_STATUS", [
      "EXTERNAL_USER_STATUS",
    ]);
    ensureProcessExists(processes, "CHECK_THIRD_PARTY_USER_STATUS", [
      "CHECK_THIRD_PARTY_USER_STATUS",
    ]);
    ensureProcessExists(processes, "MEMBER_FIRM", [
      "IDM_AND_CEM",
      "MEMBER_FIRM_STATUS",
    ]);
    ensureProcessExists(processes, "CLIENT_LEVEL_ROLE_ASSIGN", ["L2Support"]);
  };

  useEffect(() => {
    if (siteId !== null) {
      processData();
    }
  }, []);

  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const data = await createSitesClientApi.taxPortal_GetHQSiteSettings();
        setSiteSettings(data);
      } catch (error) {
        console.error("Error fetching site settings:", error);
      }
    };
    fetchSiteSettings();
  }, []);

  const cellfunction = (celldata: any) => {
    const typeMap: { [key: string]: string } = {
      PROVIDE_USERGROUP_ACCESS: "TESS Internal User Isheet",
      TESS_ISHEET_ASSIGNMENT: "TESS Isheet",
      SYNC_ISHEET: "Sync ISheet",
      SYNC_MDM: "Sync MDM Entity & Consent",
      SYNC_MDM_PROJECT: "Sync MDM Projects",
      SYNC_MDM_PHASE: "Sync MDM Phases",
      SITE_CREATION: "Create Site from template",
      SYNC_IMANAGE_WORKSPACE_PER_SITE:
        "Sync IManage Workspaces & Update Folder Metadata",
      SYNC_ENTITY_ISHEET: "Populate Legal Entities",
      SYNC_CEM: "Sync CEM",
      POPULATE_MEMBERFIRM_DROPDOWN: "Populate Member Firm Organization",
      PUSH_ORG_TO_ISHEET: "Populate Third-party Organization",
      CONTROVERSY_TRACKER: "Populate Controversy Tracker",
      IR_CHOICES: "Populate Information Request Choices",
      SYNC_CEM_HQ: "Populate Project Status",
      SYNC_SITE_SETTINGS: "Update Site Settings",
      DM_CHOICES: "Populate Document Metadata Choices",
      EXTERNAL_FOLDER_CREATION: "Create Folders",
      SYNC_FOLDER_METADATA_ISHEET: "Sync Folder Metadata iSheet",
      SYNC_INFORMATION_REQUEST_ISHEET: "Sync Information Request iSheet",
      SYNC_DOCUMENT_METADATA_ISHEET: "Sync Document Metadata iSheet",
      "CLIENT_LEVEL_ROLE_ASSIGN-L2Support": "Assign Internal User - L2 Support",
      INTERNAL_USER_ASSIGNMENT: "Assign Internal - Internal User Request Form.",
      "CREATE_EXTERNAL_USER-IDM_AND_CEM":
        "Assign External - Client User Request Form",
      "CHECK_EXTERNAL_USER_STATUS-EXTERNAL_USER_STATUS":
        "Client User - Update Status",
      "CREATE_EXTERNAL_USER_THIRD_PARTY-IDM_AND_CEM":
        "Assign User External - Third Party User Request Form",
      "CHECK_THIRD_PARTY_USER_STATUS-CHECK_THIRD_PARTY_USER_STATUS":
        "Third Party User - Update Status",
      "MEMBER_FIRM-IDM_AND_CEM":
        "Assign Member Firm User - Member Firm User Request Form",
      "MEMBER_FIRM-MEMBER_FIRM_STATUS":
        "Member Firm User - Update Status",    
    };

    const key =
      celldata.data.type +
      (celldata.data.subType ? `-${celldata.data.subType}` : "");
    return typeMap[key] || "";
  };

  const customOrder = [
    "SITE_CREATION",
    "SYNC_ISHEET",
    "SYNC_MDM",
    "SYNC_MDM_PROJECT",
    "SYNC_MDM_PHASE",
    "EXTERNAL_FOLDER_CREATION",
    "SYNC_IMANAGE_WORKSPACE_PER_SITE",
    "DM_CHOICES",
    "IR_CHOICES",
    "SYNC_FOLDER_METADATA_ISHEET",
    "SYNC_INFORMATION_REQUEST_ISHEET",
    "SYNC_DOCUMENT_METADATA_ISHEET",
    "SYNC_CEM_HQ",
    "SYNC_ENTITY_ISHEET",
    "POPULATE_MEMBERFIRM_DROPDOWN",
    "PUSH_ORG_TO_ISHEET",
    "CONTROVERSY_TRACKER",
    "SYNC_SITE_SETTINGS",
    "SYNC_CEM",
    "CLIENT_LEVEL_ROLE_ASSIGN",
    "PROVIDE_USERGROUP_ACCESS",
    "TESS_ISHEET_ASSIGNMENT",
    "INTERNAL_USER_ASSIGNMENT",
    "CREATE_EXTERNAL_USER",
    "CHECK_EXTERNAL_USER_STATUS",
    "MEMBER_FIRM",
    "CREATE_EXTERNAL_USER_THIRD_PARTY",
    "CHECK_THIRD_PARTY_USER_STATUS",
  ];

  const sortedDataSource = data?.data?.sort((a, b) => {
    return customOrder.indexOf(a.type!) - customOrder.indexOf(b.type!);
  });

  const internalSiteAssignedProcesses = [
    data?.data?.find((process) => process.type === "PROVIDE_USERGROUP_ACCESS") ??
      createProcess("PROVIDE_USERGROUP_ACCESS"),
    data?.data?.find((process) => process.type === "TESS_ISHEET_ASSIGNMENT") ??
      createProcess("TESS_ISHEET_ASSIGNMENT"),
  ];

  const displayedProcessData =
    appName === AppName.IS && selectedTab === "tab2"
      ? internalSiteAssignedProcesses
      : sortedDataSource;

  const canRun = () => {
    return sortedDataSource?.find((e) => {
      if (e.type === "SYNC_MDM" && e.status === "SUCCESS") return true;
      else return false;
    });
  };

  const isSyncMdmInProgressOrError = () => {
    return sortedDataSource?.some((e) => {
      return (
        ["SYNC_MDM", "SYNC_MDM_PHASE", "SYNC_MDM_PROJECT"].includes(e?.type!) &&
        (e.status === "ERROR" || e.status === "INPROGRESS" || e.status === null)
      );
    });
  };

  const retryButton = (celldata: any) => {
    const type = celldata.data.type;
    const subType = celldata.data.subType;
    const status = celldata.data.status;
    const roleNames = celldata.data.subType;
    const roleName = roleNames?.replace(/_/g, " ").toLowerCase();
    const roleId = roles?.find(
      (role: GetAllRolesDTO) =>
        role?.name?.toLowerCase() === roleName || role?.name?.includes("Power")
    )?.roleId;
    const showRetry = () => {
      return (
        type === "SITE_CREATION" &&
        (status === null || status === "INPROGRESS" || status === "ERROR")
      );
    };

    const showRunForProcess = () => {
      const validProcessTypes = [
        "PROVIDE_USERGROUP_ACCESS",
        "TESS_ISHEET_ASSIGNMENT",
        "SYNC_ISHEET",
        "SYNC_MDM",
        "SYNC_MDM_PROJECT",
        "SYNC_MDM_PHASE",
        "SYNC_FOLDER_METADATA_ISHEET",
        "SYNC_INFORMATION_REQUEST_ISHEET",
        "SYNC_DOCUMENT_METADATA_ISHEET",
        "SYNC_IMANAGE_WORKSPACE_PER_SITE",
        "IR_CHOICES",
        "SYNC_CEM_HQ",
        "DM_CHOICES",
        "SYNC_ENTITY_ISHEET",
        "POPULATE_MEMBERFIRM_DROPDOWN",
        "PUSH_ORG_TO_ISHEET",
        "CONTROVERSY_TRACKER",
        "SYNC_SITE_SETTINGS",
        "EXTERNAL_FOLDER_CREATION",
        "SYNC_CEM",
        "CREATE_EXTERNAL_USER",
        "CHECK_EXTERNAL_USER_STATUS",
        "CLIENT_LEVEL_ROLE_ASSIGN",
        "INTERNAL_USER_ASSIGNMENT",
        "MEMBER_FIRM",
        "CREATE_EXTERNAL_USER_THIRD_PARTY",
        "CHECK_THIRD_PARTY_USER_STATUS",
      ];
      return validProcessTypes.includes(type);
    };

    const retryProcess = async (celldata: any) => {
      if (type === "PROVIDE_USERGROUP_ACCESS" || type === "TESS_ISHEET_ASSIGNMENT") {
        const isTessInternalUserIsheet = type === "PROVIDE_USERGROUP_ACCESS";
        const payload: TpacRequestBasicMessage[] = [
          {
            hqSiteId: props?.data?.data?.hqSiteId,
            appName,
            iSheetType: isTessInternalUserIsheet
              ? "Internal User Request Form"
              : "TESS User Request Form",
          },
        ];

        try {
          showSnackbar({
            message: isTessInternalUserIsheet
              ? "Provide User Group Access In Progress"
              : "Provide User TESS Access In Progress",
          });
          await userAssignmentClientApi.userAssignment_UserAssignTpacTrigger(
            isTessInternalUserIsheet
              ? UserAssignTpac.TriggerUserAssign
              : UserAssignTpac.TessRoleAssignInitiate,
            payload
          );
          await processData();
        } catch (error) {
          showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
        }
        return;
      }

      const syncType = (() => {
        if (type === "SYNC_MDM") return ServiceBusEnum.SYNC_MDM;
        if (type === "SYNC_CEM_HQ") return ServiceBusEnum.SYNC_CEM_HQ;
        if (type === "SYNC_IMANAGE_WORKSPACE_PER_SITE")
          return ServiceBusEnum.SYNC_IMANAGE_WORKSPACE_PER_SITE;
        if (type === "IR_CHOICES") return ServiceBusEnum.IR_CHOICES;
        if (type === "SYNC_ENTITY_ISHEET")
          return ServiceBusEnum.SYNC_ENTITY_ISHEET;
        if (type === "PUSH_ORG_TO_ISHEET")
          return ServiceBusEnum.PUSH_ORG_TO_ISHEET;
        if (type === "CONTROVERSY_TRACKER")
          return ServiceBusEnum.CONTROVERSY_TRACKER;
        if (type === "SYNC_CEM") return ServiceBusEnum.SYNC_CEM;
        if (type === "SYNC_SITE_SETTINGS")
          return ServiceBusEnum.SYNC_SITE_SETTINGS;
        if (type === "DM_CHOICES") return ServiceBusEnum.DM_CHOICES;
        if (type === "EXTERNAL_FOLDER_CREATION")
          return ServiceBusEnum.EXTERNAL_FOLDER_CREATION;
        if (type === "PUSH_ORG_TO_ISHEET")
          return ServiceBusEnum.PUSH_ORG_TO_ISHEET;
        if (type === "CLIENT_LEVEL_ROLE_ASSIGN" && subType === "L2Support") {
          return null; // Only call userAssignment_DirectRoleAssignInitiate, no other API calls
        }
        if (type === "CREATE_EXTERNAL_USER" && subType === "IDM_AND_CEM") {
          return ServiceBusEnum.CREATE_EXTERNAL_USER;
        }
        if (type === "CREATE_EXTERNAL_USER")
          return ServiceBusEnum.CREATE_EXTERNAL_USER;
        if (type === "INTERNAL_USER_ASSIGNMENT" )
          return ServiceBusEnum.INTERNAL_USER_ASSIGNMENT;
        if (type === "CREATE_EXTERNAL_USER_THIRD_PARTY")
          return ServiceBusEnum.CREATE_EXTERNAL_USER_THIRD_PARTY;
        if (type === "CHECK_EXTERNAL_USER_STATUS")
          return ServiceBusEnum.CHECK_EXTERNAL_USER_STATUS;
        if (type === "CREATE_EXTERNAL_USER_THIRD_PARTY" && subType)
          return ServiceBusEnum.CREATE_EXTERNAL_USER_THIRD_PARTY;
        if (type === "CHECK_THIRD_PARTY_USER_STATUS")
          return ServiceBusEnum.CHECK_EXTERNAL_USER_STATUS;
        if (type === "SYNC_MDM_PROJECT") return ServiceBusEnum.SYNC_MDM_PROJECT;
        if (type === "SYNC_MDM_PHASE") return ServiceBusEnum.SYNC_MDM_PHASE;
        if (type === "MEMBER_FIRM" && subType === "IDM_AND_CEM") {
          return ServiceBusEnum.MEMBER_FIRM;
        }
        if (type === "MEMBER_FIRM" && subType === "ASSIGN_USER_TO_SITE") {
          return ServiceBusEnum.MEMBER_FIRM;
        }
        if (type === "MEMBER_FIRM" && subType === "MEMBER_FIRM_STATUS") {
          return ServiceBusEnum.CHECK_EXTERNAL_USER_STATUS;
        }
        if (type === "POPULATE_MEMBERFIRM_DROPDOWN") {
          return ServiceBusEnum.POPULATE_MEMBERFIRM_DROPDOWN;
        }
        return null;
      })();

      const syncIsheets = (() => {
        if (type === "SYNC_ISHEET") return "Sync Isheet";
        if (type === "SYNC_FOLDER_METADATA_ISHEET") return "Folder metadata";
        if (type === "SYNC_INFORMATION_REQUEST_ISHEET")
          return "Information Request List";
        if (type === "SYNC_DOCUMENT_METADATA_ISHEET")
          return "Documents Metadata";
      })();

      const assignmentType = (() => {
        if (subType === "ASSIGN_USER_TO_SITE") {
          return "Assign User to site";
        }
        if (subType === "IDM_AND_CEM") return "IDM and CEM";
        if (type === "CHECK_EXTERNAL_USER_STATUS") return "Check Status";
        if (
          type === "CHECK_THIRD_PARTY_USER_STATUS" &&
          subType === "CHECK_THIRD_PARTY_USER_STATUS"
        )
          if (subType === "MEMBER_FIRM_STATUS") {
            return "Check Status";
          }
        return "Check Status";
      })();
      const isForceUpdate = (() => {
        if (type === "SYNC_IMANAGE_WORKSPACE_PER_SITE") return true;
        else return false;
      })();
      const roleType = (() => {
        if (subType === "L2Support") {
          return "L2Support";
        }
      })();

      const isheetType = (() => {
        if (type === "CREATE_EXTERNAL_USER")
          return "Client User Requests";
        if (type === "INTERNAL_USER_ASSIGNMENT")
          return "Internal User Request Form";
        if (type === "CHECK_EXTERNAL_USER_STATUS")
          return "Client User Requests";
        if (type === "CREATE_EXTERNAL_USER_THIRD_PARTY")
          return "Third-party User Request Form";
        if(type ==="CHECK_THIRD_PARTY_USER_STATUS")
          return "Third-party User Request Form";
        if (type === "SYNC_ISHEETS") return "";
        if (type === "SYNC_INFORMATION_REQUEST_ISHEET")
          return "Information Request List";
        if (type === "SYNC_DOCUMENT_METADATA_ISHEET")
          return "Documents Metadata";
        if (type === "SYNC_FOLDER_METADATA_ISHEET") return "Folder metadata";
         if (type === "MEMBER_FIRM")
          return "Member Firm User Request Form";
      })();
      
      // Handle L2Support separately (only calls DirectRoleAssignInitiate)
      if (type === "CLIENT_LEVEL_ROLE_ASSIGN" && subType === "L2Support") {       
        try {
          const roleName = appName === "CS" ? "CS - L2 Support" : "L2Support";
          const payloadForL2Support = {
            hqSiteId: props?.data?.data?.hqSiteId,
            userEmail: user?.profile.preferred_username,
            roleName: roleName,
            action: 0, // UserAssignmentCEMAction.Add
            appName: appName,
            initiatedBy: user?.profile.preferred_username,
            userAssignmentStepName: "Init_DirectRoleAssignUnAssign",
            Source: "TPAC",
          };
          await userAssignmentClientApi.userAssignment_DirectRoleAssignInitiate(payloadForL2Support);
          showSnackbar({ message: SnackbarMessage.PROVIDE_APP_ACCESS });
        } catch (error) {
          showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
        }
        return; // Exit early, don't process other logic
      }

      if (syncType) {
        const ids = siteSettings.map((e: GetSiteSettingsDTO) => e.id) as
          | number[]
          | undefined;

        const data: ServiceMessage = {
          siteId: props?.data?.data?.hqSiteId,
          userName: user?.profile.preferred_username,
          eventType: syncType,
          externalUserType: assignmentType,
          roleName: roleType,
          roleId: roleId,
          isheetType: isheetType,
          mdmClientId: props?.data?.data?.mdmClientId,
          clientName: props?.data?.data?.name,
          settingsIds: ids,
          appName: appName,
          isForceUpdate: isForceUpdate,
        };
        const payload: TpacRequestBasicMessage[] = [
          {
            hqSiteId: props?.data?.data?.hqSiteId,
            appName: appName,
            iSheetType: isheetType!,
          },
        ];
        try {
          // Handle the three main user assignment processes with UserAssignTpacTrigger
          if (syncType === ServiceBusEnum.INTERNAL_USER_ASSIGNMENT && !subType) {
            await userAssignmentClientApi.userAssignment_UserAssignTpacTrigger(
              UserAssignTpac.TriggerUserAssign,
              payload
            );
          } else if (syncType === ServiceBusEnum.CREATE_EXTERNAL_USER && !subType) {
            await userAssignmentClientApi.userAssignment_UserAssignTpacTrigger(
              UserAssignTpac.TriggerUserAssign,
              payload
            );
          } else if (syncType === ServiceBusEnum.CREATE_EXTERNAL_USER_THIRD_PARTY && !subType) {
            await userAssignmentClientApi.userAssignment_UserAssignTpacTrigger(
              UserAssignTpac.TriggerUserAssign,
              payload
            );
          } else if (syncType === ServiceBusEnum.MEMBER_FIRM) {
            await userAssignmentClientApi.userAssignment_UserAssignTpacTrigger(
              UserAssignTpac.TriggerUserAssign,
              payload
            );
          } else if (syncType === ServiceBusEnum.CREATE_EXTERNAL_USER && subType) {
            await userAssignmentClientApi.userAssignment_UserAssignTpacTrigger(
              UserAssignTpac.TriggerUserAssign,
              payload
            );
          } else if (syncType === ServiceBusEnum.CREATE_EXTERNAL_USER_THIRD_PARTY && subType) {
            await userAssignmentClientApi.userAssignment_UserAssignTpacTrigger(
              UserAssignTpac.TriggerUserAssign,
              payload
            );
          } else if(syncType === ServiceBusEnum.CHECK_EXTERNAL_USER_STATUS){
            await userAssignmentClientApi.userAssignment_UserAssignTpacTrigger(
              UserAssignTpac.TriggerCheckStatus,
              payload
            );
          } else {
            await taxPortalClientApi.taxPortal_InsertTopic([data]);
          }
          if (syncType === ServiceBusEnum.SYNC_MDM) {
            showSnackbar({ message: SnackbarMessage.SYNC_MDM });
          }
          if (syncType === ServiceBusEnum.IR_CHOICES) {
            showSnackbar({ message: SnackbarMessage.SYNC_IR });
          }
          if (syncType === ServiceBusEnum.SYNC_CEM_HQ) {
            showSnackbar({ message: SnackbarMessage.SYNC_PS });
          }
          if (syncType === ServiceBusEnum.SYNC_ENTITY_ISHEET) {
            showSnackbar({
              message: SnackbarMessage.POPULATE_LEGAL_ENTITIES,
            });
          }
          if (syncType === ServiceBusEnum.PUSH_ORG_TO_ISHEET) {
            showSnackbar({
              message: SnackbarMessage.POPULATE_THIRD_PARTY_ORG,
            });
          }
          if (syncType === ServiceBusEnum.CONTROVERSY_TRACKER) {
            showSnackbar({
              message: SnackbarMessage.POPULATE_CONTROVERSY_TRACKER,
            });
          }
          if (syncType === ServiceBusEnum.SYNC_CEM) {
            showSnackbar({ message: SnackbarMessage.RETRY_SYNC_CEM });
          }
          if (syncType === ServiceBusEnum.SYNC_SITE_SETTINGS) {
            showSnackbar({ message: SnackbarMessage.SYNC_SITE_SETTINGS_RETRY });
          }
          if (syncType === ServiceBusEnum.DM_CHOICES) {
            showSnackbar({ message: SnackbarMessage.POPULATE_DM_CHOICES });
          }
          if (syncType === ServiceBusEnum.EXTERNAL_FOLDER_CREATION) {
            showSnackbar({ message: SnackbarMessage.CREATE_FOLDER });
          }
          if (
            syncType === ServiceBusEnum.CREATE_EXTERNAL_USER &&
            subType === "IDM_AND_CEM"
          ) {
            showSnackbar({ message: SnackbarMessage.IDM_AND_CEM });
          }
          if (
            syncType === ServiceBusEnum.CREATE_EXTERNAL_USER &&
            subType === "ASSIGN_USER_TO_SITE"
          ) {
            showSnackbar({ message: SnackbarMessage.ASSIGN_USER_TO_SITE });
          }
          if (
            syncType === ServiceBusEnum.CREATE_EXTERNAL_USER_THIRD_PARTY &&
            subType === "IDM_AND_CEM"
          ) {
            showSnackbar({ message: SnackbarMessage.EX_IDM_AND_CEM });
          }
          if (
            syncType === ServiceBusEnum.CREATE_EXTERNAL_USER_THIRD_PARTY &&
            subType === "ASSIGN_USER_TO_SITE"
          ) {
            showSnackbar({ message: SnackbarMessage.ASSIGN_USER_TO_SITE });
          }
          if (syncType === ServiceBusEnum.CHECK_EXTERNAL_USER_STATUS) {
            showSnackbar({
              message: SnackbarMessage.CHECK_EXTERNAL_USER_STATUS,
            });
          }
          if (type === "INTERNAL_USER_ASSIGNMENT") {
            showSnackbar({ message: SnackbarMessage.PROVIDE_APP_ACCESS });
          }
          if (
            type === "CLIENT_LEVEL_ROLE_ASSIGN" &&
            subType === "POWER_USER_DELEGATED"
          ) {
            showSnackbar({
              message: SnackbarMessage.PROVIDE_CONTENT_ADMIN_ACCESS,
            });
          }
          if (type === "SYNC_IMANAGE_WORKSPACE_PER_SITE") {
            showSnackbar({
              message: SnackbarMessage.SYNC_IMANAGE_WORKSPACE_PER_SITE,
            });
          }
          if (type === "SYNC_MDM_PROJECT") {
            showSnackbar({
              message: SnackbarMessage.SYNC_MDM_PROJECTS,
            });
          }
          if (type === "SYNC_MDM_PHASE") {
            showSnackbar({
              message: SnackbarMessage.SYNC_MDM_PHASES,
            });
          }
          if (syncType === ServiceBusEnum.POPULATE_MEMBERFIRM_DROPDOWN) {
            showSnackbar({ message: SnackbarMessage.POPULATE_MEMBER_FIRM_ORG });
          }
          if (syncType === ServiceBusEnum.MEMBER_FIRM) {
            showSnackbar({ message: SnackbarMessage.MEMBER_FIRM });
          }
        } catch (error) {
          showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
        }
      }
      if (syncIsheets) {
        await taxPortalClientApi.taxPortal_UpdateIsheetDataTrigger(
          props?.data?.data?.hqSiteId,
          syncIsheets
        );
      }
    };

    const retryForSynMDM = async (celldata: any) => {
      const data: ServiceMessage = {
        siteId: props?.data?.data?.hqSiteId,
        userName: user?.profile.preferred_username,
        eventType: ServiceBusEnum.SYNC_MDM,
        mdmClientId: props?.data?.data?.mdmClientId,
        clientName: props?.data?.data?.name,
        appName: appName,
      };
      await taxPortalClientApi.taxPortal_InsertTopic([data]);
    };

    return (
      <div>
        {showRetry() ? (
          <Link component="button" variant="body2" onClick={retryProcess}>
            Run
          </Link>
        ) : showRunForProcess() ? (
          <Link
            component="button"
            variant="body2"
            style={{
              color:
                isSyncMdmInProgressOrError() &&
                ![
                  "SYNC_MDM",
                  "SYNC_MDM_PROJECT",
                  "SYNC_MDM_PHASE",
                  "PROVIDE_USERGROUP_ACCESS",
                  "TESS_ISHEET_ASSIGNMENT",
                ].includes(
                  type
                )
                  ? "grey"
                  : "009cde",
              pointerEvents:
                isSyncMdmInProgressOrError() &&
                ![
                  "SYNC_MDM",
                  "SYNC_MDM_PROJECT",
                  "SYNC_MDM_PHASE",
                  "PROVIDE_USERGROUP_ACCESS",
                  "TESS_ISHEET_ASSIGNMENT",
                ].includes(
                  type
                )
                  ? "none"
                  : "auto",
              opacity:
                isSyncMdmInProgressOrError() &&
                ![
                  "SYNC_MDM",
                  "SYNC_MDM_PROJECT",
                  "SYNC_MDM_PHASE",
                  "PROVIDE_USERGROUP_ACCESS",
                  "TESS_ISHEET_ASSIGNMENT",
                ].includes(
                  type
                )
                  ? 0.5
                  : 1,
            }}
            onClick={              
              showRunForProcess() ? retryProcess : retryForSynMDM
            }
          >
            Run
          </Link>
        ) : null}
      </div>
    );
  };

  const createdDateTime = (e: any) => {
    if (!isNull(e.data.status) && !isEmpty(e.data.status)) {
      if (e.data.created === null) {
        return null;
      } else {
        return (
          <div style={{ display: "flex" }}>
            <div>
              <div>{convertUTCTimeToLocal(e.data.created)}</div>
            </div>
          </div>
        );
      }
    } else return null;
  };
  const modifiedDateTime = (e: any) => {
    if (!isNull(e.data.status) && !isEmpty(e.data.status)) {
      if (e.data.created === null) {
        return null;
      } else {
        return (
          <div style={{ display: "flex" }}>
            <div>
              <div>{convertUTCTimeToLocal(e.data.lastModified)}</div>
            </div>
          </div>
        );
      }
    } else return null;
  };

  const handleTabSelection = (tab: string) => {
    setSelectedTab(tab);
  }

  return (
    <div>
      <div>
        <button
          onClick={() => {
            handleTabSelection("tab1");
          }}
          style={selectedTab === "tab1" ? activeButtonStyle : buttonStyle}
        >
          Process Lists
          <div style={activeUnderlineStyle}></div>
        </button>
        <button
          onClick={() => {
            handleTabSelection("tab2");
          }}
          style={selectedTab === "tab2" ? activeButtonStyle : buttonStyle}
        >
          Assigned Processes
          <div style={activeUnderlineStyle}></div>
        </button>
      </div>
      {selectedTab === "tab1" ? (
        <ProcessListTab siteId={siteId} appName={appName} />
      ) : (
        <>
          <DataGrid
            ref={gridRef}
            {...dataGridConfig}
            dataSource={displayedProcessData}
            showBorders={true}
            allowColumnResizing={true}
            showRowLines={true}
            columnAutoWidth={true}
            height={"100%"}
            onContentReady={onInitialized}
            onOptionChanged={(e) => {
              if (e.fullName?.includes('.width') && e.value !== e.previousValue) {
                onColumnResized(e);
              }
            }}
            noDataText={
              data.pending === true && data.fullfilled === false
                ? "Loading..."
                : "No data"
            }
          >
            <Scrolling
              mode="virtual"
              scrollByContent={true}
              rowRenderingMode="virtual"
              showScrollbar={true}
            />
            {/* <Paging defaultPageSize={35} /> */}
            <Column
              dataField="type"
              width={400}
              caption="process"
              cellRender={cellfunction}
              allowSorting={true}
            />
             <Column
              width={100}
              dataField="Run Process"
              cellRender={retryButton}
            />
            <Column width={80} dataField="status" caption="Status" />
            <Column width={400} dataField="message" caption="Message" />
           
            <Column
              width={200}
              cellRender={createdDateTime}
              dataField="created"
              caption="Initiated date and time"
            />
            <Column
              width={200}
              cellRender={modifiedDateTime}
              dataField="lastModified"
              caption="Completed date and time"
            />
             <Column dataField="createdBy" caption="Initiated by" 
             calculateCellValue={(rowData: any) => rowData?.lastModifiedBy || rowData?.createdBy || ""}/>
          </DataGrid>
        </>
      )}
    </div>
  );
};
